import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { COUNTRIES, countryName, flagEmoji } from './countries';
import './Leagues.css';

/**
 * Searchable country picker storing an ISO 3166-1 alpha-2 code. Shows the
 * selected country's flag and localised name; opening it reveals a filter input
 * and a scrollable, flag-prefixed list. Selecting a row reports the code via
 * ``onChange``; the clear button reports an empty string.
 *
 * The dropdown is rendered through a portal to ``document.body`` and positioned
 * ``fixed`` at the trigger, so it escapes ancestors that clip their subtree
 * (e.g. ``.notched-card``'s ``clip-path``) and always floats above the frame.
 *
 * @param {object} props - Component props.
 * @param {string} props.value - The currently selected ISO-2 code (or '').
 * @param {Function} props.onChange - Called with the new ISO-2 code (or '').
 * @param {string} [props.placeholder] - Trigger/search placeholder text.
 */
export default function CountrySelect({ value, onChange, placeholder }) {
  const { t } = useTranslation();
  // Default placeholder lives in i18n; a default param can't call the hook.
  const ph = placeholder || t('leagues.searchCountry');
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [pos, setPos] = useState(null);
  const wrapRef = useRef(null);
  const popRef = useRef(null);

  // Position the portaled popover at the trigger; keep it aligned on scroll/resize.
  useEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const el = wrapRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setPos({ top: r.bottom + 4, left: r.left, width: r.width });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  // Close on outside click — the popover lives in a portal, so check both nodes.
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && wrapRef.current.contains(e.target)) return;
      if (popRef.current && popRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return COUNTRIES.slice(0, 80);
    return COUNTRIES.filter(
      (c) => c.name.toLowerCase().includes(q) || c.code.toLowerCase() === q,
    ).slice(0, 80);
  }, [query]);

  const pick = (code) => {
    onChange(code);
    setOpen(false);
    setQuery('');
  };

  const selectedName = value ? countryName(value) : '';

  return (
    <div className="country-select" ref={wrapRef}>
      <div className="country-select-control">
        <button
          type="button"
          className="country-select-trigger"
          onClick={() => setOpen((o) => !o)}
        >
          {value ? (
            <span className="country-select-value">
              <span className="country-select-flag">{flagEmoji(value)}</span>
              {selectedName}
            </span>
          ) : (
            <span className="country-select-placeholder">{ph}</span>
          )}
        </button>
        {value && (
          <button
            type="button"
            className="country-select-clear"
            title={t('queue.clear')}
            onClick={() => onChange('')}
          >
            ×
          </button>
        )}
      </div>
      {open && pos && createPortal(
        <div
          className="country-select-pop country-select-pop-fixed"
          ref={popRef}
          style={{ top: pos.top, left: pos.left, width: pos.width }}
        >
          <input
            autoFocus
            className="country-select-search"
            value={query}
            placeholder={ph}
            onChange={(e) => setQuery(e.target.value)}
          />
          <ul className="country-select-list">
            {matches.map((c) => (
              <li
                key={c.code}
                className={`country-select-option ${c.code === value ? 'is-active' : ''}`}
                onClick={() => pick(c.code)}
              >
                <span className="country-select-flag">{flagEmoji(c.code)}</span>
                <span className="country-select-name">{c.name}</span>
                <span className="country-select-code">{c.code}</span>
              </li>
            ))}
            {matches.length === 0 && (
              <li className="country-select-empty">{t('leagues.noCountryMatch')}</li>
            )}
          </ul>
        </div>,
        document.body,
      )}
    </div>
  );
}
